import cookies from 'js-cookie';
import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';
import URI from 'urijs';
import { windowWrapper, appQueryParameters } from '@clubmed/utils';
import { PAYMENT_STATUS_MUTATION, PAYMENT_CLEAN_CART } from 'Utils/request/requestDictionary';
import logger from 'Logger/logger';
import { useAppContext } from 'Context/ReactAppContext';
import { getApproveRoute } from 'Components/widgets/payment-info/PaymentInfo.utils';
import { startLoading } from 'Store/global-loader/globalLoader.action';
import graphqlClient from 'Utils/graphql-client/graphqlClient';
import {
  API_PAYMENT_STATUS_PENDING,
  API_PAYMENT_STATUS_SUCCESS,
  API_PAYMENT_STATUS_REFUSED_CM,
  API_PAYMENT_STATUS_REFUSED_PSP,
  API_PAYMENT_STATUS_CANCELED,
  PAYMENT_STATUS_FAILURE,
  PAYMENT_STATUS_SUCCESS,
  PAYMENT_STATUS_REFUSED_CM,
  PAYMENT_STATUS_REFUSED_PSP,
  PAYMENT_STATUS_CANCELED,
} from 'Utils/payment/constants';
import { shouldApprovePayment } from 'Utils/payment/payment.utils';
import { getStatusFromCode } from 'Utils/ple-utils/mapper';
import { asyncPolling } from 'Utils/polling/polling';
import { EVENT_TYPE_IFRAME } from 'Views/iframe-callback/IframeCallbackView';
import { BOOKING_SELECTED_RESORT_ID, BOOKING_QUERY_PARAMS } from 'Views/booking/constants';
import * as routeBuilder from 'Routes/routeBuilder';
import {
  ECYBERSOURCE_PSP_ID,
  EPAYGATE_PSP_ID,
  IXOPAY_PSP_ID,
  PLEDG_PSP_ID,
  UPLIFT_PSP_ID,
} from 'Components/widgets/payment-info/PaymentInfo.const';
import { useContext } from 'react';
import { PaymentInfoContext } from 'Components/widgets/payment-info/PaymentInfoContext';
type Props = {
  paymentProviderId: string;
  track?: (paymentId?: string) => void;
};

function isBNPL(providerId) {
  return [UPLIFT_PSP_ID, PLEDG_PSP_ID].includes(providerId);
}

function buildRedirectionPLE(
  currency,
  paymentAmountValue,
  bookingId,
  providerId,
  paymentId,
  bookingStatus,
  paymentStatus,
  locale: string,
): string {
  const { locale: LOCALE_KEY } = appQueryParameters;
  const redirectUrl = new URI(
    `/account/bookings/${bookingId || cookies.get('bookingId')}/payment-confirmation`,
  ).query({
    paymentStatus: paymentStatus,
    bookingStatusBeforePayment: getStatusFromCode(bookingStatus),
    currency: currency,
    amount: paymentAmountValue,
    provider: providerId,
    t: paymentId,
    [LOCALE_KEY]: locale,
  });
  return redirectUrl.toString();
}

function buildRedirectionBE(
  currency,
  paymentAmountValue,
  bookingId,
  providerId,
  paymentId,
  bookingStatus,
  paymentStatus,
  locale: string,
): string {
  let normalizedPaymentStatus;
  let targetUrl = 'confirmation';
  const { locale: LOCALE_KEY } = appQueryParameters;

  switch (paymentStatus) {
    case API_PAYMENT_STATUS_SUCCESS:
      normalizedPaymentStatus = PAYMENT_STATUS_SUCCESS;
      break;

    case API_PAYMENT_STATUS_REFUSED_CM:
      normalizedPaymentStatus = PAYMENT_STATUS_REFUSED_CM.toLowerCase();
      break;

    case API_PAYMENT_STATUS_REFUSED_PSP:
      normalizedPaymentStatus = PAYMENT_STATUS_REFUSED_PSP.toLowerCase();
      targetUrl = 'banking';
      break;

    case API_PAYMENT_STATUS_CANCELED:
      normalizedPaymentStatus = PAYMENT_STATUS_CANCELED.toLowerCase();
      targetUrl = 'banking';
      break;

    default:
      normalizedPaymentStatus = PAYMENT_STATUS_FAILURE;
      break;
  }

  const bookingQueryParams = cookies.get(BOOKING_QUERY_PARAMS);
  const isCmtaInCookies = !!bookingQueryParams?.includes('cm_ta');
  const queryParams = {
    ...(bookingId
      ? {
          bi: bookingId,
        }
      : null),
    paymentStatus: normalizedPaymentStatus,
    ...(isCmtaInCookies && {
      cm_ta: isCmtaInCookies,
    }),
    selectedProposalId: cookies.get(BOOKING_SELECTED_RESORT_ID),
    ...(targetUrl !== 'confirmation' ? URI.parseQuery(bookingQueryParams) : null),
    [LOCALE_KEY]: locale,
  };
  return routeBuilder.buildUrl(targetUrl, {}, queryParams);
}

export const usePaymentRedirection = ({ paymentProviderId, track }: Props) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const appContext = useAppContext();
  const { asPath } = router;
  const origin = new URI(appContext.originalUrl).origin();
  const location = new URI(origin + asPath);
  const { onPaymentProgress } = useContext(PaymentInfoContext);

  const makePaymentStatusMutation = (paymentId) => `
  mutation {
    paymentStatus(paymentId : "${paymentId}") {
      finalPaymentResponse {
        payment {
          paymentStatus
          providerId
          paymentAmount
          currency
        }
        booking {
          bookingId
        }
      }
      errors
    }
  }
`;

  const redirectToConfirmation = (event: {
    data: {
      eventType?: string;
      paymentId?: string;
      callbackUrl?: string;
      providerResponse?: any;
    };
  }) => {
    onPaymentProgress?.(location);

    if (event?.data?.eventType !== EVENT_TYPE_IFRAME) {
      return;
    }

    const { paymentId, callbackUrl, providerResponse } = event.data;
    dispatch(startLoading());
    const url = new URI(callbackUrl);
    const [, , locale, bookingStatus, isCart] = url.segment();
    const isPLE = !!bookingStatus;

    const redirectMethod = (...params) => {
      const getRedirectMethod = isPLE ? buildRedirectionPLE : buildRedirectionBE;
      const bnplQueryString = `bnpl=${paymentProviderId}`;
      return `${getRedirectMethod(...params)}${isBNPL(paymentProviderId) ? `&${bnplQueryString}` : ''}`;
    };

    let bookingId;
    let currency;
    let paymentAmountValue;
    let providerId;
    let paymentStatus;

    if (!shouldApprovePayment(paymentProviderId)) {
      if (
        ([ECYBERSOURCE_PSP_ID, PLEDG_PSP_ID, EPAYGATE_PSP_ID].includes(paymentProviderId) ||
          paymentProviderId.includes(IXOPAY_PSP_ID)) &&
        !!paymentId
      ) {
        // Poll the server for the notification status only for cybersource
        return asyncPolling({
          delay: 3000,
          // 3 seconds
          timeout: 60 * 1000,
          // 5 minutes
          pollingFunction: () =>
            graphqlClient.runNoCacheQuery(makePaymentStatusMutation(paymentId), appContext, {
              requestName: PAYMENT_STATUS_MUTATION,
            }),
          shouldPollAgain: (response) => {
            bookingId = response?.paymentStatus?.finalPaymentResponse?.booking?.bookingId;
            currency = response?.paymentStatus?.finalPaymentResponse?.payment?.currency;
            paymentAmountValue =
              response?.paymentStatus?.finalPaymentResponse?.payment?.paymentAmount;
            paymentStatus = response?.paymentStatus?.finalPaymentResponse?.payment?.paymentStatus;
            return paymentStatus === API_PAYMENT_STATUS_PENDING;
          },
          onResponse: (response) => {
            bookingId = response?.paymentStatus?.finalPaymentResponse?.booking?.bookingId || '';
            currency = response?.paymentStatus?.finalPaymentResponse?.payment?.currency;
            paymentAmountValue =
              response?.paymentStatus?.finalPaymentResponse?.payment?.paymentAmount;
            providerId = response?.paymentStatus?.finalPaymentResponse?.payment?.providerId;
            paymentStatus = response?.paymentStatus?.finalPaymentResponse?.payment?.paymentStatus;
            const redirectUrl = redirectMethod(
              currency,
              paymentAmountValue,
              bookingId,
              providerId,
              paymentId,
              bookingStatus,
              paymentStatus,
              locale,
            );

            // If the payment was successfully completed and it was initiated from the cart :
            // Send a request to the API for cleaning the Cart until the moment that they'll do their job.
            if (isCart) {
              return graphqlClient
                .runNoCacheQuery(
                  `mutation {
                      cleanCartService(bookingId : "${bookingId}") { status }
                    }`,
                  {
                    authenticationToken: cookies.get('ACCESS_TOKEN'),
                  },
                  {
                    requestName: PAYMENT_CLEAN_CART,
                  },
                )
                .then(() => (window.parent.location.href = redirectUrl))
                .catch(() => (window.parent.location.href = redirectUrl));
            }

            track?.(paymentId);
            return (window.parent.location.href = redirectUrl);
          },
        }).catch((e) => {
          logger.error(e);
          track?.();
          paymentStatus = API_PAYMENT_STATUS_REFUSED_CM;
          const redirectUrl = redirectMethod(
            currency,
            paymentAmountValue,
            bookingId,
            providerId,
            paymentId,
            bookingStatus,
            paymentStatus,
            locale,
          );
          return (window.parent.location.href = redirectUrl);
        });
      }

      // redirect to notify server route
      return windowWrapper.setLocation(
        new URI(event.data.callbackUrl).setSearch(event.data.providerResponse).toString(),
      );
    }

    // redirect to approve/notify server route (approve, maybe 3ds, then the ol' classic notify)
    windowWrapper.setLocation(
      getApproveRoute({
        appContext,
        router,
        eventParams: {
          callbackUrl,
          paymentId,
          ...providerResponse,
        },
      }),
    );
  };

  return {
    redirectToConfirmation,
  };
};
